import React from "react";
import styled from "styled-components";
import { TabHeader } from "../shared/generic";
import { FeatureRequest } from "interfaces/feature-request";
import { useTranslate } from "hooks/common/useTranslate/useTranslate";
import { getArrayValue } from "hooks/useSorting";
import moment from "moment";
import { filterByDate } from "utils/dateFilters";

const FieldGroupContainer = styled.div`
    padding: 2rem;
`;

const FieldRow = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 2rem;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const FieldItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const FieldLabel = styled.span`
    font-weight: 500;
    margin-bottom: 0.25rem;
    font-size: 18px;
`;

const FieldValue = styled.span`
    word-break: break-word;
    font-size: 18px;
`;

const getFieldValue = (
    featureRequest: FeatureRequest,
    fieldPath: keyof FeatureRequest
): React.ReactNode => {
    const value = featureRequest[fieldPath];

    const parsedArrayValue = getArrayValue(value);

    if (parsedArrayValue.length > 1) {
        return parsedArrayValue.map((val) => <div key={val}>{val}</div>);
    }

    return !!value && typeof value === "string" ? String(value) : "-";
};

const getDhVersion = (
    featureRequest: FeatureRequest,
    translate: (key: string) => string
): string => {
    if (!featureRequest.dhVersion) {
        return "-";
    }

    const planned =
        featureRequest.dhVersionType === "PLANNED"
            ? ` (${translate("featureRequests.dhVersion.planned")})`
            : "";

    return `${featureRequest.dhVersion}${planned}`;
};

const DetailsTab: React.FC<{
    featureRequest: FeatureRequest;
}> = ({ featureRequest }) => {
    const translate = useTranslate();

    const upcomingMeetingDates = filterByDate(
        featureRequest.meetings,
        (meeting) => meeting.date,
        (date) => date.isSameOrAfter(moment(), "day")
    );

    const pastMeetingDates = filterByDate(
        featureRequest.meetings,
        (meeting) => meeting.date,
        (date) => date.isBefore(moment(), "day")
    );

    const documentationCommentDate = moment.tz(
        featureRequest.documentationCommentDate,
        "Europe/Helsinki"
    );

    const businessImpact = getFieldValue(featureRequest, "businessImpact");

    return (
        <>
            <TabHeader>
                {translate("featureRequests.featureRequestDetails.additionalInfo")}
            </TabHeader>

            <FieldGroupContainer>
                <FieldRow>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.version")}
                        </FieldLabel>
                        <FieldValue>{getDhVersion(featureRequest, translate)}</FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.workEstimate")}
                        </FieldLabel>
                        <FieldValue>{getFieldValue(featureRequest, "workEstimate")}</FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.legalReference")}
                        </FieldLabel>
                        <FieldValue>{getFieldValue(featureRequest, "legalReference")}</FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.businessImpact")}
                        </FieldLabel>
                        <FieldValue>
                            {businessImpact !== "UNKNOWN"
                                ? translate(
                                      `featureRequests.businessImpact.${getFieldValue(
                                          featureRequest,
                                          "businessImpact"
                                      )}`
                                  )
                                : "-"}
                        </FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.datahubPriority")}
                        </FieldLabel>
                        <FieldValue>
                            {translate(
                                `featureRequests.priority.${getFieldValue(
                                    featureRequest,
                                    "datahubPriority"
                                )}`
                            )}
                        </FieldValue>
                    </FieldItem>
                </FieldRow>
                <FieldRow>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.businessArea")}
                        </FieldLabel>
                        <FieldValue>{getFieldValue(featureRequest, "businessArea")}</FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.processes")}
                        </FieldLabel>
                        <FieldValue>{getFieldValue(featureRequest, "processes")}</FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.upcomingMeetings")}
                        </FieldLabel>
                        <FieldValue>
                            {upcomingMeetingDates.map((date) => (
                                <div key={date}>{date}</div>
                            ))}
                        </FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.pastMeetings")}
                        </FieldLabel>
                        <FieldValue>
                            {pastMeetingDates.map((date) => (
                                <div key={date}>{date}</div>
                            ))}
                        </FieldValue>
                    </FieldItem>
                    <FieldItem>
                        <FieldLabel>
                            {translate("featureRequests.featureRequestDetails.commentPeriod")}
                        </FieldLabel>
                        <FieldValue>
                            {documentationCommentDate.isValid()
                                ? documentationCommentDate.format("DD.MM.YYYY")
                                : ""}
                        </FieldValue>
                    </FieldItem>
                </FieldRow>
            </FieldGroupContainer>
        </>
    );
};

export default DetailsTab;
